import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Service to verify and display enhanced content in courses
 */
export class ContentDisplayService {
  /**
   * Verify that enhanced content is being displayed
   */
  static async verifyEnhancedContent(moduleId: string, courseId: string): Promise<{
    isEnhanced: boolean;
    wordCount: number;
    lastUpdated: string;
  }> {
    const { data, error } = await supabase
      .from('module_content')
      .select('content_word_count, updated_at, content')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .single();

    if (error || !data) {
      logger.error('Failed to verify content:', error);
      return { isEnhanced: false, wordCount: 0, lastUpdated: '' };
    }

    const wordCount = data.content_word_count || data.content.trim().split(/\s+/).length;
    
    return {
      isEnhanced: wordCount >= 1000, // Enhanced modules have 1000+ words
      wordCount,
      lastUpdated: data.updated_at
    };
  }

  /**
   * Get all enhanced modules for a course
   */
  static async getEnhancedModulesForCourse(courseId: string): Promise<Array<{
    moduleId: string;
    wordCount: number;
    lastEnhanced: string;
  }>> {
    const { data, error } = await supabase
      .from('module_content')
      .select('module_id, content_word_count, updated_at')
      .eq('course_id', courseId)
      .gte('content_word_count', 1000)
      .order('updated_at', { ascending: false });

    if (error) {
      logger.error('Failed to get enhanced modules:', error);
      return [];
    }

    return data.map(item => ({
      moduleId: item.module_id,
      wordCount: item.content_word_count,
      lastEnhanced: item.updated_at
    }));
  }

  /**
   * Get enhancement statistics across all courses
   */
  static async getGlobalEnhancementStats(): Promise<{
    totalModules: number;
    enhancedModules: number;
    avgWordCount: number;
    recentlyEnhanced: number;
  }> {
    const [totalResult, enhancedResult, recentResult] = await Promise.all([
      supabase.from('module_content').select('*', { count: 'exact', head: true }),
      supabase.from('module_content').select('*', { count: 'exact', head: true }).gte('content_word_count', 1000),
      supabase.from('module_content').select('*', { count: 'exact', head: true })
        .gte('content_word_count', 1000)
        .gte('updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
    ]);

    const { data: avgData } = await supabase
      .from('module_content')
      .select('content_word_count')
      .gte('content_word_count', 1000);

    const avgWordCount = avgData && avgData.length > 0
      ? Math.round(avgData.reduce((sum, item) => sum + item.content_word_count, 0) / avgData.length)
      : 0;

    return {
      totalModules: totalResult.count || 0,
      enhancedModules: enhancedResult.count || 0,
      avgWordCount,
      recentlyEnhanced: recentResult.count || 0
    };
  }
}
